import React, { useState } from "react";
import { ExternalOrder } from "../../api/OrdersAPI";
import CONFIG from "../../config/config";
import InputShortText from "../InputShortText/InputShortText";
import SelectInput from "../SelectInput/SelectInput";
import TextArea from "../TextArea/TextArea";
import { useTranslation } from "react-i18next";
import 'react-datepicker/dist/react-datepicker.css';
import SectionTitle from "../SectionTitle/SectionTitle";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { format } from "date-fns";
import { isValidText } from "../../shared/validation";
import useFeatureFlag from "../../hooks/usePermissionFlag";
import { externalOrderStatusToTranslationKeys } from "../../shared/externalOrderStatusToTranslationKey";
import OrderComments from "../OrderComments/OrderComments";
import FileUploadWrapper from "../FileUploadWrapper/FileUploadWrapper";
import { toastService } from "../../services/ToastService";
import { PrivateSharedResourceLink } from "../PrivateSharedResourceLink/PrivateSharedResourceLink";
import FileList from "../FileList/FileList";
import { Icon } from "../Icon/Icon";
import fileIcon from "../../assets/images/file.svg";
import { Download, Loader2 } from "lucide-react";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import FilesAPI from "../../api/FilesAPI";
import { extractFileName } from "../PrivateResourceLink/PrivateResourceLink";
import CopyText from "../CopyText/CopyText";
import { paymentStatusToTranslationKey } from "../../shared/paymentStatusToTranslationKey";
import { PaymentStatus } from "../../types/payment-statuses";

type Props = {
  order: ExternalOrder | null,
  onOrderChange?: (value: ExternalOrder) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element
};

function ExternalOrderForm({ order, onOrderChange = () => null, isDisabled = false, actionButton }: Props) {

  const { t } = useTranslation();

  const { hasAccess } = useFeatureFlag(['update-orders']);
  const [isDownloading, setIsDownloading] = useState(false);

  const acceptsChanges = (status: string, inOrOut: string) => {
    return status !== "ACCEPTED" && inOrOut === "OUT";
  }

  const removeLink = (url: string) => {
    const updatedAttachments = order?.attachmentUrls?.filter((attachmentUrl) => attachmentUrl !== url);
    onOrderChange({ ...order, attachmentUrls: updatedAttachments } as ExternalOrder);
  }

  const removeFile = (fileName: string) => {
    const updatedFiles = order?.files?.filter((file) => file.name !== fileName);
    onOrderChange({ ...order, files: updatedFiles } as ExternalOrder);
  }

  const renameFile = (oldFile: File, newName: string, index: number) => {
    if (!order?.files) return;
    const updatedFiles = [...order.files];
    updatedFiles[index] = new File([oldFile], newName, { type: oldFile.type });
    onOrderChange({ ...order, files: updatedFiles } as ExternalOrder);
  }

  const processFile = async (files: File[]) => {
    try {
      const newFiles = [...order?.files || [], ...files];
      onOrderChange({ ...order, files: newFiles } as ExternalOrder);
    } catch (error) {
      toastService.showToast('Something went wrong adding file', 'error');
    }
  };

  const copyOrderLink = () => {
    if (!order?.id) return;
    return `${window.location.origin}/orders?partnersOrders=true&orderId=${order.id}`;
  };

  const downloadAllFiles = async () => {
    if (!order?.attachmentUrls || order.attachmentUrls.length === 0) return;
    
    setIsDownloading(true);
    
    try {
      const zip = new JSZip();
      let successCount = 0;
      
      const addFileToZip = async (fileUrl: string) => {
        try {
          const fileName = extractFileName(fileUrl);
          const response = await FilesAPI.getUrlForSharedResource(order?.id!, fileName);
          const url = response.data.url;
          
          const fileResponse = await fetch(url);
          const fileBlob = await fileResponse.blob();
          
          zip.file(fileName, fileBlob);
          return true;
        } catch (error) {
          console.error(`Error adding file to zip: ${fileUrl}`, error);
          return false;
        }
      };
      
      for (const fileUrl of order.attachmentUrls) {
        const success = await addFileToZip(fileUrl);
        if (success) successCount++;
      }
      
      const zipBlob = await zip.generateAsync({type: 'blob'});
      
      saveAs(zipBlob, `order-${order.id || 'files'}.zip`);
      
      if (successCount === order.attachmentUrls.length) {
        toastService.showToast(t("global.zip_created_successfully"));
      } else {
        toastService.showToast(
          t("global.some_files_not_added", { count: successCount, total: order.attachmentUrls.length }),
          "error"
        );
      }
    } catch (error) {
      toastService.showToast(t("global.zip_creation_failed"), "error");
      console.error("Error creating zip file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const disabledActionButton = actionButton
    ? React.cloneElement(
      actionButton,
      {},
      React.Children.map(actionButton.props.children, (child) => {
        return child ? React.cloneElement(child, { isDisabled: isDisabled }) : null;
      })
    )
    : null;

  return (
    <>
      <div className="container flex flex-wrap mb-5 space-x-0 sm:space-x-5 space-y-5 sm:space-y-0">
        <div className="flex-1 space-y-3">
          {order?.id && (
            <div className="flex items-center mb-2 space-x-2">
              {copyOrderLink() && <CopyText text={copyOrderLink()!} />}
              <span className="text-sm opacity-80">
                #{order?.id}
              </span>
            </div>
          )}
          <SectionTitle text={t("orders.orders_basket.add_form.general_data")} />
          <InputShortText
            label={"orders.external.add_form.name"}
            validationFn={(value) => !!value && isValidText(value, 1, 100)}
            onChange={c => onOrderChange({ ...order, clientName: c.target.value} as ExternalOrder)}
            errorMessage={"errors.too_long_short"}
            defaultValue={order?.clientName}
            isDisabled={isDisabled || !hasAccess || !acceptsChanges(order?.status!, order?.b2bType!)}
          />
          <InputShortText
            label={"orders.external.add_form.item_name"}
            onChange={e => onOrderChange({ ...order, dressDescription: e.currentTarget.value } as ExternalOrder)}
            defaultValue={order?.itemName || ''}
            isDisabled
          />
          <div className="flex flex-wrap space-x-5">
            <CustomDatePicker
              isDisabled={isDisabled || !hasAccess || !acceptsChanges(order?.status!, order?.b2bType!)}
              label="orders.external.add_form.delivery_time"
              value={order?.inStoreDate}
              onChange={c => onOrderChange({ ...order, inStoreDate: format(c, 'yyyy-MM-dd') } as ExternalOrder)}
            />
            <CustomDatePicker
              isDisabled={isDisabled || !hasAccess || !acceptsChanges(order?.status!, order?.b2bType!)}
              label="orders.external.add_form.wedding_date"
              value={order?.weddingDate}
              onChange={c => onOrderChange({ ...order, weddingDate: format(c, 'yyyy-MM-dd') } as ExternalOrder)}
            />
          </div>
          <SelectInput values={CONFIG.EXTERNAL_ORDERS.STATUS.map(e => { return { value: e, readableValue: t(externalOrderStatusToTranslationKeys(e)) } })}
            onChange={e => onOrderChange({ ...order, status: e.target.value } as ExternalOrder)}
            label={"orders.external.add_form.status"}
            defaultValue={order?.status}
            isDisabled={isDisabled || !hasAccess || order?.b2bType !== "IN"}
          />
          <SelectInput values={CONFIG.ORDERS.PAYMENT_STATUS.map((e: PaymentStatus) => { return { value: e, readableValue: t(paymentStatusToTranslationKey(e)) } })}
            onChange={e => onOrderChange({ ...order, paymentStatus: e.target.value as PaymentStatus } as ExternalOrder)}
            label={"orders.external.add_form.payment_status"}
            defaultValue={order?.paymentStatus}
            isDisabled={isDisabled || !hasAccess || order?.b2bType !== "IN"}
          />
          <TextArea
            label={"orders.external.add_form.notes"}
            validationFn={(value) => !!value && value.length > 3}
            onChange={e => onOrderChange({ ...order, notes: e.currentTarget.value } as ExternalOrder)}
            errorMessage={"errors.too_short"}
            defaultValue={order?.notes || ''}
            isDisabled={isDisabled || !hasAccess || !acceptsChanges(order?.status!, order?.b2bType!)}
          />
          <div>
            <div className="flex justify-between items-center">
              <p>{`${t("orders.orders_basket.add_form.files")} [${order?.attachmentUrls?.length || 0}] (${CONFIG.MAX_FILE_UPLOAD}MB):`}</p>
              {(order?.attachmentUrls ?? []).length > 0 && (
                <ButtonV2
                  onClick={downloadAllFiles}
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  {t("global.download_all")}
                </ButtonV2>
              )}
            </div>
            <FileList
              existingFiles={order?.attachmentUrls || []}
              pendingFiles={order?.files || []}
              onRemoveExisting={removeLink}
              onRemovePending={removeFile}
              onRenamePending={renameFile}
              isDisabled={isDisabled || !hasAccess}
              resourceLinkType="custom"
              renderExistingFile={(url: string) => (
                <span>
                  <Icon url={fileIcon} cssStyles="w-5" /> <PrivateSharedResourceLink orderId={order?.id!} file={url} />
                </span>
              )}
            />
            <FileUploadWrapper
              label={`${t("orders.orders_basket.add_form.files.button")}.`}
              onChange={processFile}
              isDisabled={isDisabled || !hasAccess || order?.b2bType !== "OUT"}
              allowedMultipleFiles
            />
          </div>
        </div>
        <OrderComments
          orderId={order?.id!}
          isDisabled={!order?.id || isDisabled}
        />
      </div>
      {disabledActionButton}
    </>
  );
}

export default ExternalOrderForm;
